import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  TicketIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  PaperClipIcon,
  PencilIcon,
  EyeIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { TicketIcon as TicketIconSolid } from "@heroicons/react/24/solid";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

const TicketDetail = () => {
  const { ticketId } = useParams();
  const [newComment, setNewComment] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  // Mock ticket data - in real app, fetch based on ticketId
  const ticket = {
    id: "TK-2024-001",
    title: "Unable to access payroll portal after password reset",
    description:
      "Employee cannot log into payroll system despite successful password reset. Error message indicates invalid credentials. This issue is preventing access to pay stubs and tax information. The employee has tried clearing browser cache and using different browsers but the problem persists.",
    priority: "High",
    status: "In Progress",
    category: "Technical",
    created: "2024-07-23T08:00:00Z",
    updated: "2024-07-23T10:30:00Z",
    assignedTo: "Sarah Tech",
    assignedAvatar:
      "https://images.unsplash.com/photo-1494790108755-2616b612b510?w=40&h=40&fit=crop&crop=face",
    assignedEmail: "sarah.tech@apollo.health",
    assignedPhone: "+1-555-0123",
    requester: "John Doe",
    requesterAvatar:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face",
    requesterEmail: "john.doe@apollo.health",
    requesterPhone: "+1-555-0456",
    department: "Finance",
    location: "Building A, Floor 3",
    responseTime: "1.2 hours",
    resolution: "In Progress",
    estimatedCompletion: "2024-07-23T16:00:00Z",
    tags: ["payroll", "login-issue", "urgent", "password-reset"],
    attachments: [
      {
        id: 1,
        name: "error-screenshot.png",
        size: "245 KB",
        type: "image",
        uploadedBy: "John Doe",
        uploadedAt: "2024-07-23T08:15:00Z",
      },
      {
        id: 2,
        name: "browser-console-log.txt",
        size: "12 KB",
        type: "text",
        uploadedBy: "John Doe",
        uploadedAt: "2024-07-23T08:20:00Z",
      },
    ],
  };

  // Mock comments/activity feed
  const activities = [
    {
      id: 1,
      type: "created",
      user: "John Doe",
      userAvatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face",
      timestamp: "2024-07-23T08:00:00Z",
      content: "Ticket created with priority: High",
    },
    {
      id: 2,
      type: "comment",
      user: "John Doe",
      userAvatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face",
      timestamp: "2024-07-23T08:15:00Z",
      content:
        "I have attached screenshots of the error message and browser console logs. The issue started immediately after I reset my password this morning.",
    },
    {
      id: 3,
      type: "assigned",
      user: "System",
      userAvatar: null,
      timestamp: "2024-07-23T08:30:00Z",
      content: "Ticket assigned to Sarah Tech",
    },
    {
      id: 4,
      type: "status_change",
      user: "Sarah Tech",
      userAvatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b510?w=32&h=32&fit=crop&crop=face",
      timestamp: "2024-07-23T09:15:00Z",
      content: 'Status changed from "Open" to "In Progress"',
    },
    {
      id: 5,
      type: "comment",
      user: "Sarah Tech",
      userAvatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b510?w=32&h=32&fit=crop&crop=face",
      timestamp: "2024-07-23T09:30:00Z",
      content:
        "Hi John, I'm looking into this issue. It appears to be related to session management after password resets. I'm working with the development team to implement a fix. In the meantime, please try logging in using an incognito/private browsing window.",
    },
    {
      id: 6,
      type: "comment",
      user: "John Doe",
      userAvatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face",
      timestamp: "2024-07-23T10:15:00Z",
      content:
        "Thanks Sarah! I tried the incognito window suggestion and it works! I can now access the payroll portal. Should I continue using incognito mode for now?",
    },
    {
      id: 7,
      type: "comment",
      user: "Sarah Tech",
      userAvatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b510?w=32&h=32&fit=crop&crop=face",
      timestamp: "2024-07-23T10:30:00Z",
      content:
        "Great! Yes, please use incognito mode as a temporary workaround. We're deploying a permanent fix this afternoon that should resolve the session issue. I'll update you once it's deployed and you can test with regular browsing mode.",
    },
  ];

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case "high":
        return "text-red-600 bg-red-100 border-red-200";
      case "medium":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "low":
        return "text-green-600 bg-green-100 border-green-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "open":
        return "text-blue-600 bg-blue-100 border-blue-200";
      case "in progress":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "pending review":
        return "text-orange-600 bg-orange-100 border-orange-200";
      case "resolved":
        return "text-green-600 bg-green-100 border-green-200";
      case "closed":
        return "text-gray-600 bg-gray-100 border-gray-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "open":
        return <TicketIcon className="w-4 h-4" />;
      case "in progress":
        return <ArrowPathIcon className="w-4 h-4" />;
      case "pending review":
        return <ClockIcon className="w-4 h-4" />;
      case "resolved":
        return <CheckCircleIcon className="w-4 h-4" />;
      case "closed":
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return <TicketIcon className="w-4 h-4" />;
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "created":
        return <TicketIcon className="w-4 h-4 text-blue-600" />;
      case "comment":
        return <ChatBubbleLeftRightIcon className="w-4 h-4 text-green-600" />;
      case "assigned":
        return <UserIcon className="w-4 h-4 text-purple-600" />;
      case "status_change":
        return <ArrowPathIcon className="w-4 h-4 text-orange-600" />;
      default:
        return <InformationCircleIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
    }
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      // In real app, submit comment to backend
      console.log("Adding comment:", newComment);
      setNewComment("");
    }
  };

  const handleStatusChange = (newStatus) => {
    // In real app, update status via API
    console.log("Changing status to:", newStatus);
    console.log("Ticket ID:", ticketId);
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />

      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 sm:mb-8">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center">
                  <Link
                    to="/support/tickets"
                    className="mr-3 sm:mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Link>
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <TicketIconSolid className="w-6 h-6 sm:w-8 sm:h-8 text-apollo-primary" />
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                          {ticket.id}
                        </h1>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span
                          className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(
                            ticket.priority
                          )}`}
                        >
                          <ExclamationTriangleIcon className="w-3 h-3 inline mr-1" />
                          {ticket.priority}
                        </span>
                        <span
                          className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            ticket.status
                          )}`}
                        >
                          {getStatusIcon(ticket.status)}
                          <span className="ml-1">{ticket.status}</span>
                        </span>
                      </div>
                    </div>
                    <p className="mt-2 text-sm sm:text-base text-gray-600">
                      {ticket.title}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm sm:text-base"
                  >
                    <PencilIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Edit
                  </button>
                  <Link
                    to="/support/chat"
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-apollo-primary text-white rounded-lg hover:bg-apollo-secondary transition-colors font-medium text-sm sm:text-base"
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Chat Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Ticket Description */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                    <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 text-apollo-primary mr-2" />
                    Description
                  </h2>
                </div>
                <div className="p-4 sm:p-6">
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    {ticket.description}
                  </p>
                </div>
              </div>

              {/* Attachments */}
              {ticket.attachments && ticket.attachments.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="p-4 sm:p-6 border-b border-gray-200">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                      <PaperClipIcon className="w-4 h-4 sm:w-5 sm:h-5 text-apollo-primary mr-2" />
                      Attachments ({ticket.attachments.length})
                    </h2>
                  </div>
                  <div className="p-4 sm:p-6">
                    <div className="space-y-3">
                      {ticket.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-apollo-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <PaperClipIcon className="w-3 h-3 sm:w-4 sm:h-4 text-apollo-accent" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                                {attachment.name}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-600">
                                {attachment.size} • {attachment.uploadedBy} •{" "}
                                {getTimeAgo(attachment.uploadedAt)}
                              </p>
                            </div>
                          </div>
                          <button className="p-1 sm:p-2 text-gray-400 hover:text-apollo-primary transition-colors flex-shrink-0">
                            <EyeIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Activity Feed */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                    <ChatBubbleLeftRightIcon className="w-4 h-4 sm:w-5 sm:h-5 text-apollo-primary mr-2" />
                    Activity & Comments
                  </h2>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="space-y-4 sm:space-y-6">
                    {activities.map((activity) => (
                      <div
                        key={activity.id}
                        className="flex space-x-2 sm:space-x-3"
                      >
                        <div className="flex-shrink-0">
                          {activity.userAvatar ? (
                            <img
                              src={activity.userAvatar}
                              alt={activity.user}
                              className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded-full flex items-center justify-center">
                              {getActivityIcon(activity.type)}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                            <span className="font-medium text-gray-900 text-sm sm:text-base">
                              {activity.user}
                            </span>
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-500 hidden sm:inline">
                                •
                              </span>
                              <span className="text-xs sm:text-sm text-gray-500">
                                {getTimeAgo(activity.timestamp)}
                              </span>
                              {getActivityIcon(activity.type)}
                            </div>
                          </div>
                          <p className="mt-1 text-sm sm:text-base text-gray-700 break-words">
                            {activity.content}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Comment */}
                  <div className="mt-4 sm:mt-6 border-t border-gray-200 pt-4 sm:pt-6">
                    <div className="flex space-x-2 sm:space-x-3">
                      <img
                        src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face"
                        alt="Current User"
                        className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Add a comment..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary resize-none text-sm sm:text-base"
                        />
                        <div className="mt-2 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                          <div className="flex space-x-2">
                            <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                              <PaperClipIcon className="w-4 h-4" />
                            </button>
                          </div>
                          <button
                            onClick={handleAddComment}
                            disabled={!newComment.trim()}
                            className="w-full sm:w-auto px-4 py-2 bg-apollo-primary text-white rounded-lg hover:bg-apollo-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                          >
                            Add Comment
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4 sm:space-y-6">
              {/* Ticket Information */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                    Ticket Information
                  </h2>
                </div>
                <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-500">
                      Status
                    </label>
                    <div className="mt-1">
                      <select
                        value={ticket.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary text-sm sm:text-base"
                      >
                        <option value="Open">Open</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Pending Review">Pending Review</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-500">
                      Priority
                    </label>
                    <p className="mt-1 text-sm sm:text-base text-gray-900">
                      {ticket.priority}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-500">
                      Category
                    </label>
                    <p className="mt-1 text-sm sm:text-base text-gray-900">
                      {ticket.category}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-500">
                      Created
                    </label>
                    <p className="mt-1 text-sm sm:text-base text-gray-900 flex items-center">
                      <CalendarDaysIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-gray-400" />
                      {formatDate(ticket.created)}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-500">
                      Last Updated
                    </label>
                    <p className="mt-1 text-sm sm:text-base text-gray-900 flex items-center">
                      <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-gray-400" />
                      {formatDate(ticket.updated)}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-500">
                      Response Time
                    </label>
                    <p className="mt-1 text-sm sm:text-base text-gray-900">
                      {ticket.responseTime}
                    </p>
                  </div>

                  {ticket.estimatedCompletion && (
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-gray-500">
                        Est. Completion
                      </label>
                      <p className="mt-1 text-sm sm:text-base text-gray-900">
                        {formatDate(ticket.estimatedCompletion)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* People Involved */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                    People
                  </h2>
                </div>
                <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-500">
                      Requester
                    </label>
                    <div className="mt-2 flex items-center space-x-2 sm:space-x-3">
                      <img
                        src={ticket.requesterAvatar}
                        alt={ticket.requester}
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {ticket.requester}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {ticket.department}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {ticket.location}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-3 sm:pt-4">
                    <label className="text-xs sm:text-sm font-medium text-gray-500">
                      Assigned To
                    </label>
                    <div className="mt-2 flex items-center space-x-2 sm:space-x-3">
                      <img
                        src={ticket.assignedAvatar}
                        alt={ticket.assignedTo}
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {ticket.assignedTo}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">
                          {ticket.assignedEmail}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {ticket.assignedPhone}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {ticket.tags && ticket.tags.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="p-4 sm:p-6 border-b border-gray-200">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                      <TagIcon className="w-4 h-4 sm:w-5 sm:h-5 text-apollo-primary mr-2" />
                      Tags
                    </h2>
                  </div>
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-wrap gap-2">
                      {ticket.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 sm:px-3 py-1 bg-apollo-accent/10 text-apollo-accent rounded-full text-xs sm:text-sm font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                    Actions
                  </h2>
                </div>
                <div className="p-4 sm:p-6 space-y-2 sm:space-y-3">
                  <button className="w-full px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm sm:text-base">
                    Mark as Resolved
                  </button>
                  <button className="w-full px-3 sm:px-4 py-2 bg-apollo-primary text-white rounded-lg hover:bg-apollo-secondary transition-colors font-medium text-sm sm:text-base">
                    Escalate Ticket
                  </button>
                  <button className="w-full px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm sm:text-base">
                    Print Ticket
                  </button>
                  <button className="w-full px-3 sm:px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-medium text-sm sm:text-base">
                    Close Ticket
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TicketDetail;
